/**
 * ``useLiveFrameRefresh`` — keep a REST-backed System Status query fresh off the
 * live SSE cadence (REQ-STATUS-099).
 *
 * # Why this exists
 *
 * The System display reads the same telemetry through two paths. The SSE stream
 * (:func:`useSystemStatusStream`) carries the **stoplight only** — eight fields
 * per component, deliberately bounded so every authenticated tab can hold it for
 * the session's life (REQ-STATUS-062). Everything numeric — domain metrics,
 * resource utilization, liveness, connection counts, per-connection throughput —
 * comes from the DB-backed REST reads, and :func:`useSystemStatusSnapshot` had no
 * ``refetchInterval`` because "freshness is the stream's job".
 *
 * Those two decisions are each sound and together left a hole: the stream does
 * not carry the numbers, so **nothing refreshed them**. A panel left open showed
 * its mount-time values indefinitely while the stoplights beside them updated
 * every cadence — observed on an A deployment as a consumer backlog frozen at
 * ~5400 for minutes that jumped to ~6900 on a browser refresh.
 *
 * # Why frame-triggered rather than an interval
 *
 * A new SSE frame means the collector published a new snapshot, so the REST rows
 * behind it have moved. Two properties follow, neither of which a timer has:
 *
 * - **A stalled collector is never read in a loop.** No frames, no refreshes —
 *   and the rollup badge independently goes ``Stale`` (REQ-STATUS-016), so the
 *   operator learns the feed died rather than watching fresh-looking stale rows.
 * - **It tracks the deployment's configured cadence** (``MERIDIAN_STATUS_EMIT_
 *   INTERVAL_SECONDS``) without a second copy of that number in the UI.
 *
 * # The floor is per QUERY, not per caller
 *
 * ``minIntervalMs`` is enforced against the query's own last attempt — TanStack's
 * ``dataUpdatedAt`` / ``errorUpdatedAt`` — rather than against a ref owned by
 * this hook instance. Several components subscribe to one System Status query
 * (the panel and the Entity Details status sections share the snapshot), and a
 * per-instance floor let each of them spend the budget separately: the live pass
 * measured **8 reads in 25 s** against a 5 s floor that predicts 5. Reading the
 * query's own state means N subscribers cost what 1 does, and mount suppression
 * comes free — a query that has just fetched is inside its own floor.
 *
 * Using ``max(dataUpdatedAt, errorUpdatedAt)`` rather than ``dataUpdatedAt``
 * alone keeps a persistently failing query from being retried on every frame.
 *
 * # It refuses to run for a panel nobody can see
 *
 * Read from :func:`useOwningPanelVisible`, not from any caller. That distinction
 * is load-bearing: ``system-status`` sits in the **default layout**
 * (``presets.ts``) in a tab group whose active view is the map, and dockview
 * keeps non-active tabs mounted — so "the panel's hooks are running" is true in
 * essentially every session, including ones where the operator never opened it.
 * The first cut of this gated on mountedness alone and the live pass measured
 * four snapshot reads in 25 s with the panel never opened: every authenticated
 * tab polling, which is the cost profile that argued against widening the SSE
 * frame in the first place.
 *
 * Taking it from context rather than a parameter is deliberate — a caller that
 * forgets to pass it would reintroduce exactly that, silently.
 *
 * A panel that becomes visible again re-reads on the next frame rather than
 * instantly — bounded by the cadence, since its stale data is at most one
 * collector interval behind once the first frame lands.
 *
 * Cross-references:
 *   - REQ-STATUS-099 (detail freshness); REQ-STATUS-020/-021 (the REST reads);
 *     REQ-STATUS-022 (the SSE feed); REQ-STATUS-062 (the bounded stream).
 *   - REQ-STATUS-016 (never a false green — the ``Stale`` badge this relies on).
 *   - ADR-035 D8; ADR-016 §1 (snapshot-then-SSE); ADR-024 §2 (the panel layout).
 */
import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOwningPanelVisible } from "@/multi-pane/panel-visibility-context";
import { useSystemStatusLiveFrame } from "@/system-status/use-system-status-stream";

/** Options for :func:`useLiveFrameRefresh`. */
export interface LiveFrameRefreshOptions {
  /**
   * The owning query's key. The freshness floor is measured against **this
   * query's** last attempt, so every subscriber shares one budget.
   */
  queryKey: readonly unknown[];
  /** The query's refetch. Called at most once per live frame, and never more
   * often than ``minIntervalMs`` across all subscribers. */
  refetch: () => void;
  /**
   * Minimum wall-clock ms between re-reads of this query. A floor on the read
   * rate, so a retuned (faster) emit cadence cannot turn into a database read
   * per frame.
   */
  minIntervalMs: number;
  /**
   * The owning query's own ``enabled`` (authorization, nothing-selected).
   * Visibility is NOT passed here — it is read from the panel context, so a
   * caller cannot forget it. Defaults to true.
   */
  enabled?: boolean;
}

/**
 * Refetch a System Status REST query when the live stream publishes a new frame.
 *
 * Issues nothing while no frame is cached (a dead or not-yet-connected stream),
 * nothing for an effect re-entry that does not carry a *new* frame, nothing
 * within the query's own floor (which covers mount, since the query has just
 * fetched), and nothing while disabled.
 *
 * @param options - See {@link LiveFrameRefreshOptions}.
 *
 * @example
 * ```ts
 * const query = useQuery({ queryKey: KEY, queryFn, enabled });
 * useLiveFrameRefresh({
 *   queryKey: KEY,
 *   refetch: query.refetch,
 *   minIntervalMs: 5_000,
 *   enabled,
 * });
 * ```
 */
export function useLiveFrameRefresh({
  queryKey,
  refetch,
  minIntervalMs,
  enabled = true,
}: LiveFrameRefreshOptions): void {
  const live = useSystemStatusLiveFrame();
  const queryClient = useQueryClient();
  // Visibility is read here rather than passed by each caller: the callers are
  // several levels above this hook and a forgotten prop costs a silent poll
  // rather than a visible bug. See the module docstring.
  const panelVisible = useOwningPanelVisible();

  // The last frame receipt this hook has acted on: an advancing `receivedAt`,
  // not the effect running, is what counts as a new frame.
  //
  // The effect re-enters on any dependency change, and the one that moves in
  // practice is `refetch`'s identity (TanStack produces a new one when the
  // owning query's options change) — with the frame unmoved, no read is owed.
  // Note what does NOT re-enter it, because both look like they would: a bare
  // re-render reads the cache through `useSyncExternalStore`, which returns the
  // same object; and re-publishing a deep-equal frame hits TanStack's
  // structural sharing, which hands back the PREVIOUS reference. Tests written
  // against either shape pass without exercising this guard at all — both were
  // tried and the break probe caught both.
  const lastSeenReceivedAtRef = useRef<number | null>(null);

  // Callers build keys inline (`componentHistoryKey(id, window)`), so the array
  // identity changes every render. Depend on its serialization instead, or the
  // effect re-enters on every render for no reason.
  const keyHash = useMemo(() => JSON.stringify(queryKey), [queryKey]);

  useEffect(() => {
    if (!enabled || !panelVisible) return;

    const receivedAt = live?.receivedAt ?? null;
    if (receivedAt === null) return;
    if (receivedAt === lastSeenReceivedAtRef.current) return;
    lastSeenReceivedAtRef.current = receivedAt;

    // The floor, read off the QUERY rather than this instance — see the module
    // docstring. `errorUpdatedAt` is folded in so a failing query is retried at
    // the floor rather than on every frame.
    const state = queryClient.getQueryState(JSON.parse(keyHash) as readonly unknown[]);
    const lastAttemptAt = Math.max(state?.dataUpdatedAt ?? 0, state?.errorUpdatedAt ?? 0);
    if (Date.now() - lastAttemptAt < minIntervalMs) return;

    refetch();
  }, [live, enabled, panelVisible, minIntervalMs, refetch, queryClient, keyHash]);
}
